import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Clock, FileCheck, Phone, Mail, Building } from 'lucide-react';

const ComplaintHandlingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#003D5C] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/" className="inline-flex items-center text-blue-200 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Complaint Handling Process</h1>
          <p className="text-blue-200 mt-2">Our commitment to resolving your concerns</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          <div className="bg-[#F0F7FF] p-6 rounded-xl mb-8 border-l-4 border-[#0077B6]">
            <p className="text-gray-700 m-0">
              At Utmost Financial Inc., we are committed to providing excellent service. If you have a concern or complaint, we want to hear from you and will work to resolve it promptly and fairly.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Our Commitment</h2>
            <p className="text-gray-600">
              We take all complaints seriously and are committed to:
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>Treating all complaints with respect and confidentiality</li>
              <li>Responding promptly and keeping you informed throughout the process</li>
              <li>Investigating complaints thoroughly and fairly</li>
              <li>Taking appropriate action to resolve issues</li>
              <li>Learning from complaints to improve our services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">How to Submit a Complaint</h2>
            
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="w-12 h-12 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">By Phone</h3>
                <p className="text-gray-600 text-sm">1-877-239-9060</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">By Email</h3>
                <p className="text-gray-600 text-sm">complaints@utmostfinancial.ca</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="w-12 h-12 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">In Writing</h3>
                <p className="text-gray-600 text-sm">A-970 Sargent Ave, Winnipeg, MB R3E0E3</p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Complaint Resolution Process</h2>
            
            <div className="space-y-6 mt-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-[#0077B6] rounded-full flex items-center justify-center text-white font-bold">1</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="h-5 w-5 text-[#0077B6]" />
                    <h3 className="font-bold text-gray-800">Acknowledgment</h3>
                  </div>
                  <p className="text-gray-600">We will acknowledge receipt of your complaint within 2 business days and provide you with a reference number.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#003D5C] font-bold">2</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-5 w-5 text-[#D4AF37]" />
                    <h3 className="font-bold text-gray-800">Investigation</h3>
                  </div>
                  <p className="text-gray-600">Our compliance team will investigate your complaint thoroughly, reviewing all relevant documentation and speaking with involved parties.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-[#0077B6] rounded-full flex items-center justify-center text-white font-bold">3</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FileCheck className="h-5 w-5 text-[#0077B6]" />
                    <h3 className="font-bold text-gray-800">Resolution</h3>
                  </div>
                  <p className="text-gray-600">We aim to provide a final response within 30 days. If more time is needed, we will keep you informed of the progress.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Escalation Options</h2>
            <p className="text-gray-600 mb-4">
              If you are not satisfied with our response, you may escalate your complaint to the following regulatory bodies:
            </p>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-2">For Mortgage Complaints (Manitoba)</h3>
                <p className="text-gray-600 text-sm">
                  <strong>Manitoba Securities Commission</strong><br />
                  500-400 St. Mary Avenue, Winnipeg, MB R3C 4K5<br />
                  Phone: 204-945-2548 | Website: mbsecurities.ca
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-2">For Mortgage Complaints (Alberta)</h3>
                <p className="text-gray-600 text-sm">
                  <strong>Real Estate Council of Alberta (RECA)</strong><br />
                  202, 1506 - 11 Avenue SW, Calgary, AB T3C 0M9<br />
                  Phone: 403-228-2954 | Website: reca.ca
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-2">For Insurance Complaints</h3>
                <p className="text-gray-600 text-sm">
                  <strong>OmbudService for Life & Health Insurance</strong><br />
                  Phone: 1-888-295-8112 | Website: olhi.ca
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">What to Include in Your Complaint</h2>
            <p className="text-gray-600">To help us resolve your complaint efficiently, please include:</p>
            <ul className="text-gray-600 space-y-2">
              <li>Your full name and contact information</li>
              <li>A clear description of your concern</li>
              <li>Relevant dates, names, and account/policy numbers</li>
              <li>Copies of any relevant documents</li>
              <li>What resolution you are seeking</li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
};

export default ComplaintHandlingPage;
